import { attachCallAudioOutput } from "./call-audio-route";

/** MPEG byte streams use the browser's decoder, retaining pitch-corrected playback. */
export function canStreamSpeech(): boolean {
  return typeof MediaSource !== "undefined" && MediaSource.isTypeSupported("audio/mpeg");
}

export async function playStreamedSpeech(
  response: Response,
  options: { signal: AbortSignal; rate: number; onPlaying: () => void; maxBytes: number },
): Promise<void> {
  const { signal } = options;
  if (signal.aborted) throw signal.reason;
  if (!response.body) throw new Error("Voice response is empty.");
  const reader = response.body.getReader();
  const media = new MediaSource();
  const url = URL.createObjectURL(media);
  const audio = new Audio(url);
  const output = attachCallAudioOutput(audio);
  audio.playbackRate = options.rate;
  audio.preservesPitch = true;
  const controller = new AbortController();
  const playbackSignal = AbortSignal.any([signal, controller.signal]);
  let total = 0;
  let started = false;
  try {
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener("abort", aborted);
        controller.abort();
        if (error) reject(error);
        else resolve();
      };
      const aborted = () => finish(signal.reason ?? new Error("Speech cancelled."));
      signal.addEventListener("abort", aborted, { once: true });
      audio.onended = () => finish();
      audio.onerror = () => finish(new Error("Could not play that clip."));
      audio.onplaying = () => {
        if (started || settled) return;
        started = true;
        options.onPlaying();
      };
      const pump = async () => {
        await Promise.all([mediaEvent(media, "sourceopen", playbackSignal), output.ready]);
        if (playbackSignal.aborted) return;
        const buffer = media.addSourceBuffer("audio/mpeg");
        const declared = Number(response.headers.get("content-length") ?? 0);
        if (declared > options.maxBytes) throw new Error("Voice response is too large.");
        let playing = false;
        while (!playbackSignal.aborted) {
          const next = await reader.read();
          if (playbackSignal.aborted) return;
          if (next.done) break;
          total += next.value.byteLength;
          if (total > options.maxBytes) throw new Error("Voice response is too large.");
          const appended = mediaEvent(buffer, "updateend", playbackSignal, () => {
            buffer.appendBuffer(new Uint8Array(next.value));
          });
          await appended;
          if (!playing) {
            playing = true;
            // Awaiting play here deadlocks decoders that need another chunk before starting.
            void audio.play().catch(finish);
          }
        }
        if (!total) throw new Error("Voice response is empty.");
        if (!playbackSignal.aborted && media.readyState === "open") media.endOfStream();
      };
      void pump().catch(finish);
      if (signal.aborted) aborted();
    });
  } finally {
    output.release();
    controller.abort();
    audio.onended = null;
    audio.onerror = null;
    audio.onplaying = null;
    audio.pause();
    audio.src = "";
    void reader.cancel().catch(() => undefined);
    URL.revokeObjectURL(url);
  }
}

function mediaEvent(
  target: EventTarget,
  event: string,
  signal: AbortSignal,
  action?: () => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      target.removeEventListener(event, done);
      target.removeEventListener("error", failed);
      signal.removeEventListener("abort", aborted);
    };
    const done = () => {
      cleanup();
      resolve();
    };
    const failed = () => {
      cleanup();
      reject(new Error("Could not play that clip."));
    };
    const aborted = () => {
      cleanup();
      reject(signal.reason ?? new Error("Speech cancelled."));
    };
    target.addEventListener(event, done, { once: true });
    target.addEventListener("error", failed, { once: true });
    signal.addEventListener("abort", aborted, { once: true });
    if (signal.aborted) aborted();
    else {
      try {
        action?.();
      } catch (error) {
        cleanup();
        reject(error);
      }
    }
  });
}
